import { useState, useEffect } from "react";
import { apiClient } from "../../../lib/api";
import { useAppStore } from "../../../store/useAppStore";

interface SupportIncidentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedConversation: any;
  recipientId: string;
  recipientName: string;
  recipientRole: string;
  onSuccess: () => void;
}

const SupportIncidentModal = ({
  isOpen,
  onClose,
  selectedConversation,
  recipientId,
  recipientName,
  recipientRole,
  onSuccess,
}: SupportIncidentModalProps) => {
  const [supportMessageBody, setSupportMessageBody] = useState("");
  const [isSendingSupport, setIsSendingSupport] = useState(false);

  const addNotification = useAppStore((state) => state.addNotification);

  useEffect(() => {
    if (isOpen) {
      setSupportMessageBody("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSendFromSupport = async () => {
    if (!supportMessageBody.trim() || !recipientId || isSendingSupport) return;
    setIsSendingSupport(true);

    try {
      let safeBookId = selectedConversation?.booking?.bookId ?? selectedConversation?.bookId;;
      
      const payload = {
        bookId: safeBookId, 
        messageTypeId: 1,
        body: supportMessageBody,
        userId: recipientId,
      };

      const response: any = await apiClient.post("Messaging/FromSupport", payload);

      if (response.statusCode === "200" && response.data) {
        addNotification({
          type: "success",
          message: "Notificación enviada exitosamente",
        });
        onSuccess();
        onClose();
      } else {
        addNotification({
          type: "error",
          message: response?.resultError?.message || "Error al enviar el mensaje",
        });
      }
    } catch (error) {
      console.error("Error sending support message:", error);
      addNotification({
        type: "error",
        message: "Ocurrió un error de conexión",
      });
    } finally {
      setIsSendingSupport(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4 transition-opacity">
      <div className="bg-white rounded-[12px] p-6 w-full max-w-md shadow-xl">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-[18px] font-semibold text-neutral-text">
            Notificar incidencia
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-neutral-bg rounded-full transition-colors"
          >
            <span className="material-symbols-outlined text-neutral-secondary text-[20px]">
              close
            </span>
          </button>
        </div>

        {/* DESTINATARIO */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-neutral-text mb-2">
            Destinatario de la notificación
          </label>
          <div className="w-full px-4 py-3 bg-neutral-bg border border-neutral-border rounded-md text-sm text-neutral-text flex items-center gap-3">
            <span className="material-symbols-outlined text-primary text-[20px]">
              person
            </span>
            <div className="flex flex-col">
              <span className="font-semibold">{recipientRole}</span>
              <span className="text-neutral-secondary text-xs">{recipientName}</span>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-neutral-text mb-2">
            Mensaje
          </label>
          <textarea
            className="w-full h-28 px-4 py-3 bg-white border border-neutral-border rounded-md focus:outline-none focus:border-primary text-sm text-neutral-text placeholder:text-neutral-secondary resize-none"
            placeholder={`Escribe el mensaje que se enviará al ${recipientRole.toLowerCase()}...`}
            value={supportMessageBody}
            onChange={(e) => setSupportMessageBody(e.target.value)}
          ></textarea>
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-sm font-medium text-neutral-text hover:bg-neutral-bg rounded-md transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSendFromSupport}
            disabled={isSendingSupport || !supportMessageBody.trim() || !recipientId}
            className="px-5 py-2.5 text-sm font-medium bg-primary text-white rounded-md hover:bg-ui-primary-hover transition-colors active:scale-95 disabled:opacity-50 disabled:active:scale-100 flex items-center gap-2"
          >
            {isSendingSupport ? (
              <span className="material-symbols-outlined animate-spin text-[18px]">
                progress_activity
              </span>
            ) : (
              <span className="material-symbols-outlined text-[18px]">send</span>
            )}
            {isSendingSupport ? "Enviando..." : "Enviar notificación"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupportIncidentModal;